import {
  createRouter,
  createWebHistory,
  type RouteLocation,
  type RouteComponent,
  type RouteRecordRaw,
  type Router,
  type RouteLocationRaw,
} from "vue-router";

import { useSession } from "@/composables/session";

const view =
  (name: string): RouteComponent =>
  () =>
    import(`@/views/${name}.vue`);

export const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: view("LoginView"),
    meta: { public: true },
  },
  {
    path: "/no-workspace",
    name: "no-workspace",
    component: view("NoWorkspaceView"),
  },
  {
    path: "/workspaces/:workspaceId",
    name: "hub",
    component: view("HubView"),
  },
  {
    path: "/workspaces/:workspaceId/operations",
    name: "operations",
    component: view("WorkspaceView"),
  },
  {
    path: "/workspaces/:workspaceId/operations/tools/:toolKey",
    name: "toolbox-tool",
    component: view("ToolboxToolView"),
  },
  {
    path: "/workspaces/:workspaceId/catalogs",
    name: "catalogs",
    component: view("CatalogView"),
  },
  {
    path: "/workspaces/:workspaceId/artifacts",
    name: "platform-artifacts",
    component: view("PlatformDataView"),
  },
  {
    path: "/workspaces/:workspaceId/jobs",
    name: "platform-jobs",
    component: view("PlatformDataView"),
  },
  {
    path: "/workspaces/:workspaceId/audit-logs",
    name: "platform-activity",
    component: view("PlatformDataView"),
  },
  {
    path: "/workspaces/:workspaceId/mcp-tokens",
    name: "mcp-tokens",
    component: view("McpTokensView"),
  },
  {
    path: "/workspaces/:workspaceId/uploads",
    name: "uploads",
    component: view("UploadView"),
  },
  {
    path: "/workspaces/:workspaceId/uploads/rejections",
    name: "upload-rejections",
    component: view("UploadRejectionsView"),
  },
  {
    path: "/workspaces/:workspaceId/inventory",
    name: "inventory",
    component: view("InventoryView"),
  },
  {
    path: "/workspaces/:workspaceId/database-exports",
    name: "database-exports",
    redirect: (to: RouteLocation) => ({
      name: "inventory",
      params: { workspaceId: to.params.workspaceId },
      query: { tab: "exports" },
    }),
  },
  { path: "/", redirect: "/no-workspace" },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

export function installSessionGuard(target: Router) {
  target.beforeEach(async (to): Promise<RouteLocationRaw | boolean | void> => {
    let state;
    try {
      state = await useSession().load();
    } catch {
      return to.meta.public ? true : { name: "login" };
    }

    if (!state.authenticated && !to.meta.public) return { name: "login" };
    if (state.authenticated && to.name === "login") {
      const preferred = localStorage.getItem("omni.workspace");
      const workspace =
        state.workspaces.find((item) => item.id === preferred) ??
        state.workspaces[0];
      return workspace
        ? { name: "hub", params: { workspaceId: workspace.id } }
        : { name: "no-workspace" };
    }
    if (
      state.authenticated &&
      to.name === "no-workspace" &&
      state.workspaces.length
    ) {
      const preferred = localStorage.getItem("omni.workspace");
      const workspace =
        state.workspaces.find((item) => item.id === preferred) ??
        state.workspaces[0];
      if (!workspace) return { name: "no-workspace" };
      return { name: "hub", params: { workspaceId: workspace.id } };
    }
    if (to.params.workspaceId) {
      const found = state.workspaces.some(
        (item) => item.id === to.params.workspaceId,
      );
      if (!found) return { name: "no-workspace" };
      localStorage.setItem("omni.workspace", String(to.params.workspaceId));
    }
  });
}

installSessionGuard(router);
